import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ImageUp, Loader2, Zap, ZapOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OPTIONS_LECTURE, chargerLecteur, choisirCode } from "@/lib/barcode";
import { lireCode } from "@/lib/colisCode";

const LARGEUR_MAX = 1600; // px analysés par image (au-delà : plus lent, pas plus fiable)

/**
 * Caméra arrière + lecture continue des codes-barres. Appelle
 * onDetected({ text, format }) dès qu'un code est lu. Repli : « Prendre une
 * photo » (appareil photo du téléphone ou fichier image).
 *
 * continu : la caméra reste ouverte et enchaîne les colis (listes) ; un même
 * code n'est renvoyé qu'une fois toutes les 4 s. `retour` = { texte, ok, t }
 * affiche le résultat du dernier ajout sur l'image. onCancel facultatif.
 */
const DELAI_MEME_CODE = 4000;

/**
 * passes (facultatif) : réglages essayés à tour de rôle, une image sur deux
 * avec chacun, ex. [{ echelle: 1 }, { echelle: 0.72, binarizer: "GlobalHistogram" }].
 * Un code 2D (PDF417) pris en photo de biais ou un peu flou ne se lit souvent
 * qu'à une certaine taille d'image ou avec un autre seuillage noir / blanc.
 */
/* haute : caméra en haute définition (codes 2D denses).
 * secours = { accepter(texte), apresMs } : si seul un code « de secours »
 * (ex. n° de suivi) est visible pendant apresMs, on le renvoie quand même
 * avec { secours: true }. aideIgnore : texte affiché quand un code est ignoré. */
export function BarcodeScanner({ onDetected, onCancel, continu = false, retour = null, accepter = null, options = OPTIONS_LECTURE, aide = null, priorite = null, passes = null,
  haute = false, secours = null, aideIgnore = "visez l'airwaybill" }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const actif = useRef(true);
  const [etat, setEtat] = useState("demarrage"); // demarrage | camera | erreur
  const [erreur, setErreur] = useState("");
  const [torche, setTorche] = useState(null); // null = non disponible
  // Zoom de la caméra (si le téléphone le permet) : en haute définition, 2× par
  // défaut -> le code 2D occupe plus de pixels et on tient le téléphone plus
  // loin (mise au point plus facile).
  const [zoom, setZoom] = useState(null); // { val, min, max } ; null = non disponible
  const [analysePhoto, setAnalysePhoto] = useState(false);
  const [photoVide, setPhotoVide] = useState(false);
  const [ignore, setIgnore] = useState(""); // dernier code lu qui n'est pas un airwaybill
  const dernier = useRef(""); // ITF : même lecture 2 fois de suite avant de valider
  const recents = useRef({}); // continu : code -> heure du dernier envoi
  const cb = useRef(onDetected);
  cb.current = onDetected;
  const accRef = useRef(accepter);
  accRef.current = accepter;
  const optsRef = useRef(options);
  optsRef.current = options;
  const prioRef = useRef(priorite);
  prioRef.current = priorite;
  const passesRef = useRef(passes);
  passesRef.current = passes;
  const nImage = useRef(0);
  const secoursRef = useRef(secours);
  secoursRef.current = secours;
  const vuSecours = useRef(null); // { texte, depuis }
  const precedents = useRef(new Set()); // codes vus à l'image précédente (ITF)
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!retour) return undefined;
    setFlash(retour);
    const t = setTimeout(() => setFlash(null), 1800);
    return () => clearTimeout(t);
  }, [retour]);

  const arreter = useCallback(() => {
    actif.current = false;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const trouve = useCallback((r) => {
    if (!continu) arreter();
    try { navigator.vibrate?.(80); } catch { /* sans vibreur */ }
    cb.current({ text: r.text, format: r.format, secours: !!r.secours });
  }, [arreter, continu]);

  useEffect(() => {
    actif.current = true;
    let timer = null;
    const lecteur = chargerLecteur();

    async function boucle() {
      if (!actif.current) return;
      const v = videoRef.current;
      const c = canvasRef.current;
      if (v && c && v.readyState >= 2 && v.videoWidth) {
        const ps = passesRef.current;
        const pass = ps?.length ? ps[nImage.current++ % ps.length] : null;
        // cadre : seulement la bande du viseur, à la résolution de la caméra
        // (plus de pixels par module pour un code 2D dense).
        const [sx, sy, sw, sh] = pass?.cadre
          ? [v.videoWidth * 0.04, v.videoHeight * 0.22, v.videoWidth * 0.92, v.videoHeight * 0.56]
          : [0, 0, v.videoWidth, v.videoHeight];
        const k = Math.min(1, (pass?.cadre ? 2400 : LARGEUR_MAX) / sw) * (pass?.echelle || 1);
        c.width = Math.round(sw * k);
        c.height = Math.round(sh * k);
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(v, sx, sy, sw, sh, 0, 0, c.width, c.height);
        try {
          const { readBarcodes } = await lecteur;
          const res = await readBarcodes(ctx.getImageData(0, 0, c.width, c.height),
            pass?.binarizer ? { ...optsRef.current, binarizer: pass.binarizer } : optsRef.current);
          const r = choisirCode(res, lireCode, accRef.current);
          if (r && !r.ignore && actif.current) {
            // Le code ITF (TNT national) n'a pas de clé de contrôle : on
            // attend 2 lectures identiques pour éviter un chiffre mal lu.
            if (!continu) {
              if (r.format !== "ITF" || dernier.current === r.text) { trouve(r); return; }
              dernier.current = r.text;
            } else {
              // Continu : tous les codes acceptés de l'image (ex. étiquette du
              // chariot + colis), les prioritaires d'abord, chacun une seule
              // fois toutes les 4 s.
              const t = Date.now();
              const vus = new Set();
              let envoye = false;
              const liste = [r, ...(r.autres || [])].sort((a, b) => (prioRef.current ? prioRef.current(a.text) - prioRef.current(b.text) : 0));
              for (const x of liste) {
                vus.add(x.text);
                if (x.format === "ITF" && !precedents.current.has(x.text)) continue;
                if (!recents.current[x.text] || t - recents.current[x.text] > DELAI_MEME_CODE) { trouve(x); envoye = true; }
                recents.current[x.text] = t; // toujours visé : on prolonge
              }
              precedents.current = vus;
              if (envoye) { if (actif.current) timer = setTimeout(boucle, 600); return; }
            }
          } else if (r?.ignore) {
            setIgnore(r.ignores[0]);
            const sc = secoursRef.current;
            const t = sc && r.ignores.find((x) => sc.accepter(x));
            if (t) {
              if (vuSecours.current?.texte !== t) vuSecours.current = { texte: t, depuis: Date.now() };
              else if (Date.now() - vuSecours.current.depuis > (sc.apresMs || 3000) && !continu) {
                trouve({ text: t, format: "secours", secours: true }); return;
              }
            }
          }
        } catch (e) {
          if (actif.current) { setErreur("Le lecteur de codes-barres n'a pas pu se charger. Rechargez la page."); setEtat("erreur"); }
          return;
        }
      }
      if (actif.current) timer = setTimeout(boucle, 120);
    }

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setErreur(window.isSecureContext
          ? "Cet appareil ne permet pas d'utiliser la caméra dans le navigateur."
          : "La caméra n'est accessible que si le site est ouvert en https://.");
        setEtat("erreur");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" }, width: { ideal: haute ? 3840 : 1920 }, height: { ideal: haute ? 2160 : 1080 } },
        });
        if (!actif.current) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        v.srcObject = stream;
        await v.play().catch(() => {});
        const track = stream.getVideoTracks()[0];
        // Mise au point continue si l'appareil la propose.
        const caps = track.getCapabilities?.() || {};
        if (caps.focusMode?.includes?.("continuous")) track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
        if (caps.torch) setTorche(false);
        if (caps.zoom && caps.zoom.max > 1) {
          const val = haute ? Math.min(2, caps.zoom.max) : (caps.zoom.min || 1);
          if (val > (caps.zoom.min || 1)) track.applyConstraints({ advanced: [{ zoom: val }] }).catch(() => {});
          setZoom({ val, min: caps.zoom.min || 1, max: caps.zoom.max });
        }
        setEtat("camera");
        boucle();
      } catch (e) {
        setErreur(e?.name === "NotAllowedError"
          ? "Accès à la caméra refusé. Autorisez la caméra pour ce site dans le navigateur, ou utilisez « Prendre une photo »."
          : e?.name === "NotFoundError" ? "Aucune caméra trouvée sur cet appareil."
            : "Impossible d'ouvrir la caméra (déjà utilisée par une autre application ?).");
        setEtat("erreur");
      }
    })();
    return () => { clearTimeout(timer); arreter(); };
  }, [arreter, trouve, continu, haute]);

  const changerZoom = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || !zoom) return;
    const pas = [zoom.min, 2, 3].filter((z, i, a) => z <= zoom.max && a.indexOf(z) === i);
    const val = pas[(pas.indexOf(zoom.val) + 1) % pas.length] ?? zoom.min;
    try { await track.applyConstraints({ advanced: [{ zoom: val }] }); setZoom({ ...zoom, val }); } catch { setZoom(null); }
  };

  const basculerTorche = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try { await track.applyConstraints({ advanced: [{ torch: !torche }] }); setTorche(!torche); } catch { setTorche(null); }
  };

  const photo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAnalysePhoto(true); setPhotoVide(false);
    try {
      const { readBarcodes } = await chargerLecteur();
      let r = choisirCode(await readBarcodes(file, optsRef.current), lireCode, accRef.current);
      const ps = passesRef.current;
      if ((!r || r.ignore) && ps?.length && typeof createImageBitmap === "function") {
        // Photo : chaque réglage à son tour (taille rapportée à 1600 px de large).
        const img = await createImageBitmap(file);
        const c = document.createElement("canvas");
        for (const pass of ps) {
          const k = Math.min(1, (pass.cadre ? 2600 : LARGEUR_MAX) / img.width) * (pass.echelle || 1);
          c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
          const ctx = c.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, c.width, c.height);
          const x = choisirCode(await readBarcodes(ctx.getImageData(0, 0, c.width, c.height),
            pass.binarizer ? { ...optsRef.current, binarizer: pass.binarizer } : optsRef.current), lireCode, accRef.current);
          if (x && !x.ignore) { r = x; break; }
        }
      }
      if (r && !r.ignore) trouve(r); else setPhotoVide(true);
    } catch { setPhotoVide(true); } finally { setAnalysePhoto(false); }
  };

  return (
    <div className="space-y-3" data-testid="barcode-scanner">
      <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
        <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 w-full h-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />
        {etat === "camera" && (
          <>
            {/* Viseur : le code-barres doit tenir dans le cadre, bien à plat. */}
            <div className="absolute inset-x-[8%] top-1/2 -translate-y-1/2 h-[38%] rounded-lg border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
              <div className="absolute inset-x-3 top-1/2 h-0.5 bg-red-500/90 animate-pulse" />
            </div>
            <p className="absolute bottom-2 inset-x-2 text-center text-xs text-white/90 drop-shadow" data-testid="scanner-aide">
              {ignore
                ? <>Code ignoré (<span className="font-mono">{ignore.length > 24 ? ignore.slice(0, 24) + "…" : ignore}</span>) : {aideIgnore}</>
                : (aide || "Placez l'airwaybill (FedEx, TNT) dans le cadre")}
            </p>
            {flash && (
              <div className={`absolute top-2 left-2 right-12 rounded-md px-3 py-2 text-sm font-semibold shadow-lg ${flash.ok ? "bg-emerald-600 text-white" : "bg-amber-500 text-black"}`} data-testid="scanner-flash">
                {flash.texte}
              </div>
            )}
            {zoom && (
              <button type="button" onClick={changerZoom} className="absolute top-2 left-2 h-9 min-w-9 px-2 rounded-full bg-black/50 text-white text-sm font-semibold" aria-label="Zoom" data-testid="scanner-zoom">
                {Math.round(zoom.val * 10) / 10}×
              </button>
            )}
            {torche !== null && (
              <button type="button" onClick={basculerTorche} className="absolute top-2 right-2 p-2 rounded-full bg-black/50 text-white" aria-label="Lampe">
                {torche ? <ZapOff size={18} /> : <Zap size={18} />}
              </button>
            )}
          </>
        )}
        {etat === "demarrage" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 text-sm">
            <Loader2 className="animate-spin" /> Ouverture de la caméra…
          </div>
        )}
        {etat === "erreur" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-white/90 text-sm" data-testid="scanner-erreur">
            <Camera className="opacity-70" /> {erreur}
          </div>
        )}
      </div>
      {photoVide && <p className="text-sm text-red-600 dark:text-red-400">Aucun airwaybill FedEx ou TNT trouvé sur la photo. Reprenez-la de plus près, bien à plat et nette, en cadrant le code-barres du n° de colis.</p>}
      <div className="flex flex-wrap gap-2 justify-between">
        <label className="inline-flex">
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={photo} data-testid="scanner-photo" />
          <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-input text-sm cursor-pointer hover:bg-accent">
            {analysePhoto ? <Loader2 size={15} className="animate-spin" /> : <ImageUp size={15} />} Prendre une photo
          </span>
        </label>
        {onCancel && <Button type="button" variant="ghost" onClick={() => { arreter(); onCancel(); }}>{continu ? "Fermer la caméra" : "Retour à la saisie"}</Button>}
      </div>
    </div>
  );
}
